import { useCallback, useEffect, useRef, useState } from "react";
import AppHeader from "@/components/AppHeader";
import RouteBuilder from "@/components/RouteBuilder";
import CostInputs from "@/components/CostInputs";
import CarrierBids, { newBid } from "@/components/CarrierBids";
import UnitDetails from "@/components/UnitDetails";
import Schedule from "@/components/Schedule";
import ComparisonCards from "@/components/ComparisonCards";
import RFPModal from "@/components/RFPModal";
import QuoteDocModal from "@/components/QuoteDocModal";
import SaveQuoteModal from "@/components/SaveQuoteModal";
import DriverSendModal from "@/components/DriverSendModal";
import NearbyQuotes from "@/components/NearbyQuotes";
import CarrierAdmin from "@/components/CarrierAdmin";
import QuoteHistory from "@/components/QuoteHistory";
import RfpLog from "@/components/RfpLog";
import AiChat from "@/components/AiChat";
import { QuoteActions } from "@/components/QuoteActions";
import { DEFAULT_COST_INPUTS, COMPANY, SINGLE_DROP_MARGIN, MULTI_DROP_MARGIN, MULTI_DROP_BONUS_PER_DROP } from "@/lib/pricing";
import { newUnit, newStop, MOWER_TYPES } from "@/data/options";
import { buildQuoteHtml } from "@/lib/quote";
import { emptyCustomer } from "@/components/CustomerSection";
import { usePricing } from "@/hooks/usePricing";
import { useEtas } from "@/hooks/useEtas";
import api from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_PICKUP = "623 N Main St, Paris, TX 75460";

export default function Calculator() {
  const [pickup, setPickup] = useState(DEFAULT_PICKUP);
  const [stops, setStops] = useState([newStop()]);
  const [routeData, setRouteData] = useState(null);
  const [manualMiles, setManualMiles] = useState("");

  const [cost, setCost] = useState(DEFAULT_COST_INPUTS);
  const [overnight, setOvernight] = useState({ enabled: false, hotelCost: 110, mealPerDiem: 40, nights: 1 });
  const [overnightTouched, setOvernightTouched] = useState(false);
  const [backhaul, setBackhaul] = useState({ enabled: false, revenue: "" });
  const [bonus, setBonus] = useState({ type: "flat", perDrop: 15, drops: 1, pctAmount: 10 });

  const [carriers, setCarriers] = useState([]);
  const [carrierBids, setCarrierBids] = useState([newBid()]);
  const [bidMarginPct, setBidMarginPct] = useState("");

  const [units, setUnits] = useState([newUnit()]);
  const [photos, setPhotos] = useState([]);
  const [salesAgents, setSalesAgents] = useState([]);
  const [customers, setCustomers] = useState([emptyCustomer()]);
  const [mowerCost, setMowerCost] = useState("");
  const [schedule, setSchedule] = useState({
    pickupDate: "",
    deliveryDate: "",
    liftgatePickup: false,
    liftgateDelivery: false,
  });

  const [rfpOpen, setRfpOpen] = useState(false);
  const [quoteDocOpen, setQuoteDocOpen] = useState(false);
  const [driverSendOpen, setDriverSendOpen] = useState(false);
  const [fuelMarket, setFuelMarket] = useState(null);

  useEffect(() => {
    api.get("/fuel-price").then(({ data }) => setFuelMarket(data)).catch(() => {});
  }, []);
  const [carrierAdminOpen, setCarrierAdminOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rfpLogOpen, setRfpLogOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiSessionId] = useState(() => "chat-" + Math.random().toString(36).slice(2));
  const [saving, setSaving] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);

  const loadCarriers = useCallback(() => {
    api.get("/carriers").then(({ data }) => setCarriers(data)).catch(() => {});
  }, []);
  useEffect(() => {
    loadCarriers();
  }, [loadCarriers]);

  const stopValues = stops.map((s) => s.value);
  const { numStops, effectiveMiles, result } = usePricing({
    stopValues,
    manualMiles,
    routeData,
    cost,
    overnight,
    backhaul,
    bonus,
    carrierBids,
    bidMarginPct,
  });

  // Auto-suggest overnight when route exceeds 10 hours (unless user overrode); nights = 1 per 10h.
  const suggestOvernight = result.totalRouteHours > 10;
  const suggestedNights = Math.max(1, Math.floor(result.totalRouteHours / 10));
  useEffect(() => {
    if (!overnightTouched) {
      setOvernight((o) =>
        o.enabled === suggestOvernight && (!suggestOvernight || o.nights === suggestedNights)
          ? o
          : { ...o, enabled: suggestOvernight, nights: suggestOvernight ? suggestedNights : o.nights }
      );
    }
  }, [suggestOvernight, suggestedNights, overnightTouched]);

  // Margin/bonus defaults by drop count: multi-drop 40% + $100/drop bonus, single drop 25%.
  const prevStops = useRef(null);
  useEffect(() => {
    if (prevStops.current === numStops) return;
    const first = prevStops.current === null;
    prevStops.current = numStops;
    if (numStops > 1) {
      setCost((c) => ({ ...c, marginPct: MULTI_DROP_MARGIN }));
      setBonus((b) => ({ ...b, type: "flat", perDrop: MULTI_DROP_BONUS_PER_DROP, drops: numStops }));
      toast.info(`Multi-drop defaults: ${MULTI_DROP_MARGIN}% margin, $${MULTI_DROP_BONUS_PER_DROP}/drop bonus ($${MULTI_DROP_BONUS_PER_DROP * numStops})`);
    } else if (!first) {
      setCost((c) => ({ ...c, marginPct: SINGLE_DROP_MARGIN }));
      setBonus((b) => ({ ...b, type: "flat", perDrop: 15, drops: 1 }));
    }
  }, [numStops]);

  // In-house delivery estimate, per-stop ETAs and per-customer price splits.
  const { deliveryEstimate, stopEtas, stopSplits } = useEtas({
    routeData,
    stops,
    numStops,
    stopTimeMin: cost.stopTimeMin,
    pickupDate: schedule.pickupDate,
    recommendation: result.recommendation,
    customers,
  });

  const wrappedSetOvernight = (v) => {
    setOvernightTouched(true);
    setOvernight(v);
  };

  // Display object for PDF templates (footer "your K&G sales agent(s) …").
  const salesAgentDisplay = {
    name: salesAgents.map((a) => a.name).filter(Boolean).join(", "),
    email: salesAgents.map((a) => a.email).filter(Boolean).join(", "),
  };
  const rfpData = { pickup, stops: stopValues, effectiveMiles, schedule, units, photos, salesAgents, salesAgent: salesAgentDisplay, customers, mowerCost, stopSplits, stopEtas };

  // Driver route sheet: stops with customer name + phone + ETA (no pricing).
  const routeSheetData = {
    pickup,
    drops: stops
      .filter((s) => s.value.trim())
      .map((s, i) => ({
        address: s.value,
        customer: s.customer || customers[i]?.name || "",
        phone: customers[i]?.phone || "",
        eta: stopEtas[i]?.date || "",
      })),
    effectiveMiles,
    totalRouteHours: result.totalRouteHours || 0,
    schedule,
    units,
    bonusAmount: result.inhouse?.bonusAmount || 0,
  };

  const mowerType = [...new Set(units.map((u) => MOWER_TYPES.find((t) => t.value === u.type)?.label || u.type))].join(", ");

  const openSaveQuote = () => {
    if (!result.recommendation) {
      toast.error("Fill in at least one shipping option before saving");
      return;
    }
    setSaveOpen(true);
  };

  const saveQuote = async ({ clientName, status, addonNote }) => {
    setSaving(true);
    const validStops = stopValues.filter((s) => s.trim());
    const routeSummary = `${numStops} stop${numStops === 1 ? "" : "s"} · ${
      validStops[0] || "route"
    }${numStops > 1 ? ` +${numStops - 1}` : ""}`;
    try {
      await api.post("/quotes", {
        route_summary: routeSummary,
        chosen_method: result.recommendation.method,
        final_price: result.recommendation.price,
        total_miles: effectiveMiles,
        client_name: clientName,
        agent_name:
          salesAgentDisplay.name ||
          [...new Set(customers.map((c) => c.agent?.name).filter(Boolean))].join(" / ") ||
          "",
        mower_type: mowerType,
        mower_cost: Number(mowerCost) || 0,
        status,
        route_addon_note: addonNote,
        quote_html: buildQuoteHtml({
          ...rfpData,
          price: result.recommendation.price,
          methodLabel: result.recommendation.method,
          stopEtas: result.recommendation.key === "inhouse" ? stopEtas : [],
        }),
        data: {
          pickup, stops: stopValues, stopCustomers: stops.map((s) => s.customer || ""), stopSplits,
          selectedCarrier: result.outsourced?.carrierName || "",
          routeData, manualMiles, cost, overnight, backhaul, bonus,
          carrierBids, bidMarginPct, units, photos, schedule, salesAgents, customers, mowerCost,
        },
      });
      toast.success("Quote saved to history");
      setSaveOpen(false);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Tick/untick a nearby saved quote to add/remove its drop on the current route.
  const toggleAddonStop = (q, checked) => {
    if (checked) {
      setStops((prev) => [...prev, { ...newStop(q.near_stop), customer: q.client_name || "" }]);
      toast.info("Add-on stop added — recalculate the route to reprice");
    } else {
      setStops((prev) => {
        const next = prev.filter((s) => s.value !== q.near_stop);
        return next.length ? next : [newStop()];
      });
    }
  };

  const reopenQuote = (d) => {
    // Prevent the multi-drop defaults effect from overriding the reopened quote's saved margin/bonus.
    prevStops.current = (d.stops || []).filter((s) => s && s.trim()).length;
    setPickup(d.pickup ?? DEFAULT_PICKUP);
    setStops(
      d.stops?.length
        ? d.stops.map((v, i) => ({ ...newStop(v), customer: d.stopCustomers?.[i] || "" }))
        : [newStop()]
    );
    setRouteData(d.routeData ?? null);
    setManualMiles(d.manualMiles ?? "");
    setCost(d.cost ? { ...DEFAULT_COST_INPUTS, ...d.cost } : DEFAULT_COST_INPUTS);
    setOvernight(d.overnight ? { nights: 1, ...d.overnight } : { enabled: false, hotelCost: 110, mealPerDiem: 40, nights: 1 });
    setOvernightTouched(true);
    setBackhaul(d.backhaul ?? { enabled: false, revenue: "" });
    setBonus(
      d.bonus
        ? d.bonus.perDrop != null
          ? d.bonus
          : { ...d.bonus, perDrop: d.bonus.flatAmount ?? 15, drops: 1 }
        : { type: "flat", perDrop: 15, drops: 1, pctAmount: 10 }
    );
    setCarrierBids(
      d.carrierBids?.length
        ? d.carrierBids
        : d.vendorQuote
          ? [{
              id: "legacy",
              carrierId: d.selectedCarrierId || "",
              carrierName: carriers.find((c) => c.id === d.selectedCarrierId)?.name || "",
              rate: d.vendorQuote,
            }]
          : [newBid()]
    );
    setBidMarginPct(d.bidMarginPct ?? "");
    setUnits(d.units?.length ? d.units : [newUnit()]);
    setPhotos(d.photos ?? []);
    setSalesAgents(d.salesAgents ?? (d.salesAgent?.email ? [d.salesAgent] : []));
    setCustomers(
      d.customers?.length ? d.customers : d.customer ? [d.customer] : [emptyCustomer()]
    );
    setMowerCost(d.mowerCost ?? "");
    setSchedule(d.schedule ?? { pickupDate: "", deliveryDate: "", liftgatePickup: false, liftgateDelivery: false });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <AppHeader
        onOpenHistory={() => setHistoryOpen(true)}
        onOpenCarriers={() => setCarrierAdminOpen(true)}
        onOpenAI={() => setAiOpen(true)}
      />

      <main className="max-w-[1500px] mx-auto px-4 md:px-8 py-6 space-y-6">
        {/* Route */}
        <section>
          <h2 className="text-lg font-bold text-[#1C1917] mb-3">1 · Route Builder</h2>
          <RouteBuilder
            pickup={pickup}
            setPickup={setPickup}
            stops={stops}
            setStops={setStops}
            routeData={routeData}
            setRouteData={setRouteData}
            manualMiles={manualMiles}
            setManualMiles={setManualMiles}
            totalRouteHours={result.totalRouteHours || 0}
          />
          <NearbyQuotes routeData={routeData} stops={stops} onToggleAddon={toggleAddonStop} />
        </section>

        {/* Inputs grid */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <div>
            <h2 className="text-lg font-bold text-[#1C1917] mb-3">2 · Cost Inputs</h2>
            <CostInputs
              cost={cost}
              setCost={setCost}
              overnight={overnight}
              setOvernight={wrappedSetOvernight}
              suggestOvernight={suggestOvernight}
              backhaul={backhaul}
              setBackhaul={setBackhaul}
              bonus={bonus}
              setBonus={setBonus}
              fuelMarket={fuelMarket}
            />
          </div>
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-bold text-[#1C1917] mb-3">3 · Carrier Bids</h2>
              <CarrierBids
                carriers={carriers}
                bids={carrierBids}
                setBids={setCarrierBids}
                marginPct={bidMarginPct}
                setMarginPct={setBidMarginPct}
                pickup={pickup}
                destination={stopValues.find((s) => s.trim()) || ""}
                units={units}
              />
            </div>
            <Schedule
            schedule={schedule}
            setSchedule={setSchedule}
            deliveryEstimate={deliveryEstimate}
            stopEtas={stopEtas}
            onSendToDriver={() => setDriverSendOpen(true)}
          />
          </div>
        </section>

        {/* Comparison */}
        <section>
          <h2 className="text-lg font-bold text-[#1C1917] mb-3">4 · Three-Way Comparison</h2>
          <ComparisonCards result={result} />
        </section>

        {/* Units */}
        <section>
          <h2 className="text-lg font-bold text-[#1C1917] mb-3">5 · Equipment</h2>
          <UnitDetails
            units={units}
            setUnits={setUnits}
            photos={photos}
            setPhotos={setPhotos}
            salesAgents={salesAgents}
            setSalesAgents={setSalesAgents}
            customers={customers}
            setCustomers={setCustomers}
            maxCustomers={Math.max(1, numStops)}
            mowerCost={mowerCost}
            setMowerCost={setMowerCost}
            shippingPrice={result.recommendation?.price}
          />
        </section>

        {/* Actions */}
        <QuoteActions
          onRfp={() => setRfpOpen(true)}
          onQuoteDoc={() => setQuoteDocOpen(true)}
          onSave={openSaveQuote}
          saving={saving}
          onDriver={() => setDriverSendOpen(true)}
          onRfpLog={() => setRfpLogOpen(true)}
        />
      </main>

      <RFPModal open={rfpOpen} onOpenChange={setRfpOpen} rfpData={rfpData} carriers={carriers} />
      <DriverSendModal open={driverSendOpen} onOpenChange={setDriverSendOpen} routeSheetData={routeSheetData} />
      <QuoteDocModal open={quoteDocOpen} onOpenChange={setQuoteDocOpen} rfpData={rfpData} result={result} />
      <SaveQuoteModal
        open={saveOpen}
        onOpenChange={setSaveOpen}
        agentName={salesAgentDisplay.name}
        mowerType={mowerType}
        price={result.recommendation?.price}
        method={result.recommendation?.method}
        defaultClientName={
          customers.map((c) => c.name).filter((n) => n && n.trim()).join(" / ") ||
          stops.map((s) => s.customer).filter((c) => c && c.trim()).join(" / ")
        }
        onSave={saveQuote}
        saving={saving}
      />
      <CarrierAdmin
        open={carrierAdminOpen}
        onOpenChange={setCarrierAdminOpen}
        carriers={carriers}
        reload={loadCarriers}
      />
      <QuoteHistory open={historyOpen} onOpenChange={setHistoryOpen} onReopen={reopenQuote} />
      <RfpLog open={rfpLogOpen} onOpenChange={setRfpLogOpen} />
      <AiChat open={aiOpen} onOpenChange={setAiOpen} sessionId={aiSessionId} />
    </div>
  );
}
